import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { cn } from "@/lib/utils";

const RHFCheckboxGroup = ({
    name,
    label,
    options = [],
    getOptionValue = (o) => o.value,
    getOptionLabel = (o) => o.label,
    getOptionDisabled = () => false,
    getOptionHint = () => null,
    sanitizeSelected = (next) => next,
    className,
    required = false,
}) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const error = errors?.[name];

    return (
        <div className={cn("w-full", className)}>
            {label && (
                <label className="block text-sm mb-1.5 font-medium text-text-secondary">
                    {label}
                    {required && <span className="text-red-400 ml-1">*</span>}
                </label>
            )}

            <Controller
                name={name}
                control={control}
                rules={{ required: required ? "This field is required" : false }}
                render={({ field }) => {
                    const selected = Array.isArray(field.value) ? field.value : [];
                    const toggle = (opt, value) => {
                        if (getOptionDisabled(opt, selected)) return;
                        const next = selected.includes(value)
                            ? selected.filter((v) => v !== value)
                            : [...selected, value];
                        field.onChange(sanitizeSelected(next, opt, selected));
                    };

                    return (
                        <div className="space-y-2">
                            {options.map((opt, idx) => {
                                const value = getOptionValue(opt);
                                const checked = selected.includes(value);
                                const disabled = getOptionDisabled(opt, selected);
                                const hint = getOptionHint(opt, selected);
                                return (
                                    <label
                                        key={idx}
                                        title={hint || undefined}
                                        className={cn(
                                            "flex items-center gap-2 text-xs text-text-primary/90 select-none",
                                            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={disabled}
                                            onChange={() => toggle(opt, value)}
                                        />
                                        <span className="truncate">{getOptionLabel(opt)}</span>
                                        {hint && (
                                            <span className="text-[10px] text-text-secondary italic truncate">
                                                ({hint})
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    );
                }}
            />

            {error?.message && <p className="text-red-400 text-xs mt-1">{error.message}</p>}
        </div>
    );
};

export default RHFCheckboxGroup;

